/**
 * @file Components/Sections/TravelDeals.jsx
 * @description Time-limited travel deals banner with countdown timer,
 * gradient cards, and urgency-driven CTAs.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Flame, Zap, Tag } from 'lucide-react';

const deals = [
  {
    title: 'Kashmir Winter Special',
    subtitle: '5 Nights / 6 Days with flights',
    original: '₹24,999',
    price: '₹16,999',
    discount: '32% OFF',
    image: 'https://images.unsplash.com/photo-1597074866923-dc0589150458?w=500&q=80',
    tag: 'Limited Seats',
    gradient: 'from-sky-600 to-blue-800',
  },
  {
    title: 'Goa Beach Getaway',
    subtitle: '3 Nights / 4 Days all inclusive',
    original: '₹12,999',
    price: '₹7,999',
    discount: '38% OFF',
    image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=500&q=80',
    tag: 'Best Seller',
    gradient: 'from-orange-500 to-red-600',
  },
  {
    title: 'Rajasthan Royal Circuit',
    subtitle: '6 Nights / 7 Days heritage tour',
    original: '₹28,999',
    price: '₹19,999',
    discount: '31% OFF',
    image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=500&q=80',
    tag: 'New Launch',
    gradient: 'from-amber-500 to-orange-600',
  },
];

const Countdown = () => {
  const [time, setTime] = useState({ h: 23, m: 59, s: 59 });

  useEffect(() => {
    const endTime = new Date();
    endTime.setHours(23, 59, 59, 999);

    const tick = () => {
      const now = new Date();
      const diff = endTime - now;
      if (diff <= 0) return;
      setTime({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="flex items-center gap-1.5">
      {[time.h, time.m, time.s].map((v, i) => (
        <React.Fragment key={i}>
          <span className="bg-white/20 backdrop-blur-sm text-white font-mono font-black text-lg px-2.5 py-1 rounded-lg min-w-[40px] text-center">
            {pad(v)}
          </span>
          {i < 2 && <span className="text-white/60 font-bold">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

const TravelDeals = () => (
  <section className="pt-8 pb-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header with countdown */}
      <div className="bg-gradient-to-r from-[#1a2463] to-[#4f5fd6] rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
            <Flame size={24} className="text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Flash Deals</h2>
            <p className="text-white/60 text-sm">Grab these before they're gone</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden md:block">
            <p className="text-white/50 text-xs font-medium">Ends in</p>
          </div>
          <Countdown />
        </div>
      </div>

      {/* Deal Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {deals.map((deal, i) => (
          <Link
            key={i}
            to="/tours"
            className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100"
          >
            {/* Image */}
            <div className="relative h-48 overflow-hidden">
              <img src={deal.image} alt={deal.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
              <div className={`absolute inset-0 bg-gradient-to-t ${deal.gradient} opacity-40`} />

              {/* Discount Badge */}
              <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-black px-3 py-1 rounded-full flex items-center gap-1">
                <Zap size={10} /> {deal.discount}
              </span>
              <span className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                {deal.tag}
              </span>
            </div>

            {/* Content */}
            <div className="p-5">
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#243185] transition-colors">{deal.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{deal.subtitle}</p>

              <div className="flex items-end justify-between mt-4">
                <div>
                  <span className="text-sm text-gray-400 line-through">{deal.original}</span>
                  <p className="text-2xl font-black text-gray-900">{deal.price}</p>
                  <p className="text-[11px] text-gray-400">per person</p>
                </div>
                <div className="flex items-center gap-1 text-[#243185] font-semibold text-sm group-hover:gap-2 transition-all">
                  Book Now <ArrowRight size={14} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default TravelDeals;
